import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { conversationId } from '../../helpers/conversationId';
import { directMessagesPath } from '../../helpers/directMessagesPath';
import { describeEffectiveScore } from '../../helpers/reputationVouchDisplay';
import DomainBadges from '../UserProfile/DomainBadges';
import { VouchButton } from '../Vouch/VouchButton';
import type {
  HovercardPosition,
  HovercardProfile,
  HovercardState,
} from './useHovercard';
import './Hovercard.scss';

interface HovercardProps {
  state: HovercardState;
  position: HovercardPosition;
  cardHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  onFollow: (userId: string) => Promise<void>;
  onUnfollow: (userId: string) => Promise<void>;
}

function buildDefaultAvatar(username: string): string {
  const initial = username.charAt(0).toUpperCase();
  const colors = [
    '#f87171',
    '#fb923c',
    '#fbbf24',
    '#a3e635',
    '#34d399',
    '#22d3ee',
    '#60a5fa',
    '#a78bfa',
    '#f472b6',
  ];
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = colors[Math.abs(hash) % colors.length];
  const svg = `<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="24" fill="${color}"/><text x="24" y="30" font-family="Arial,sans-serif" font-size="18" font-weight="600" text-anchor="middle" fill="white">${initial}</text></svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

function SkeletonContent() {
  return (
    <>
      <div className="tex-hovercard__header">
        <div className="tex-hovercard__skeleton-avatar" />
        <div className="tex-hovercard__info">
          <div
            className="tex-hovercard__skeleton-line"
            style={{ height: 16, width: '60%', marginBottom: 6 }}
          />
          <div
            className="tex-hovercard__skeleton-line"
            style={{ height: 13, width: '40%' }}
          />
        </div>
      </div>
      <div
        className="tex-hovercard__skeleton-line"
        style={{ height: 13, width: '100%', marginBottom: 6 }}
      />
      <div
        className="tex-hovercard__skeleton-line"
        style={{ height: 13, width: '80%', marginBottom: 16 }}
      />
      <div className="tex-hovercard__skeleton-btn" />
    </>
  );
}

interface ProfileContentProps {
  profile: HovercardProfile;
  onFollow: (userId: string) => Promise<void>;
  onUnfollow: (userId: string) => Promise<void>;
}

function ProfileContent({
  profile,
  onFollow,
  onUnfollow,
}: ProfileContentProps) {
  const [followPending, setFollowPending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleFollowClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (followPending) return;
    setFollowPending(true);
    try {
      if (profile.isFollowedByViewer) {
        await onUnfollow(profile.username);
      } else {
        await onFollow(profile.username);
      }
    } finally {
      setFollowPending(false);
    }
  };

  const defaultAvatar = buildDefaultAvatar(profile.username);

  // TRIL-403: show the effective (earned + vouch boost) score with a compact
  // "+N vouched" indicator. null when the user has no reputation to show.
  const rep =
    profile.reputationOverall != null
      ? describeEffectiveScore(
          profile.reputationOverall,
          profile.reputationVouchBoost ?? 0,
        )
      : null;

  return (
    <>
      <div className="tex-hovercard__header">
        <img
          src={profile.avatar || defaultAvatar}
          alt={profile.displayName}
          className="tex-hovercard__avatar"
          onError={(e) => {
            (e.target as HTMLImageElement).src = defaultAvatar;
          }}
        />
        <div className="tex-hovercard__info">
          <p className="tex-hovercard__name">{profile.displayName}</p>
          <p className="tex-hovercard__handle">@{profile.username}</p>
        </div>
      </div>

      {profile.bio && <p className="tex-hovercard__bio">{profile.bio}</p>}

      <div className="tex-hovercard__stats">
        <div className="tex-hovercard__stat">
          <span className="tex-hovercard__stat-number">
            {profile.followerCount}
          </span>
          <span className="tex-hovercard__stat-label">Followers</span>
        </div>
        <div className="tex-hovercard__stat">
          <span className="tex-hovercard__stat-number">
            {profile.followingCount}
          </span>
          <span className="tex-hovercard__stat-label">Following</span>
        </div>
      </div>

      {rep && (
        <div
          className="tex-hovercard__reputation"
          data-testid="hovercard-reputation-strip"
        >
          <span className="tex-hovercard__rep-chip">{rep.effectiveShown}</span>
          {rep.hasBoost && (
            <span
              className="tex-hovercard__vouch-indicator"
              data-testid="hovercard-vouch-indicator"
            >
              +{rep.vouchedShown} vouched
            </span>
          )}
          <DomainBadges domains={profile.reputationDomains} />
        </div>
      )}

      <button
        type="button"
        className={`tex-hovercard__follow-btn ${
          profile.isFollowedByViewer
            ? 'tex-hovercard__follow-btn--following'
            : 'tex-hovercard__follow-btn--follow'
        }`}
        disabled={followPending}
        onClick={handleFollowClick}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {followPending
          ? profile.isFollowedByViewer
            ? 'Unfollowing…'
            : 'Following…'
          : profile.isFollowedByViewer
            ? 'Following'
            : 'Follow'}
      </button>
      {/* TRIL-402: start-a-DM entry point. Hidden on your own hovercard. */}
      {user?.id && profile.userId && profile.userId !== user.id && (
        <button
          type="button"
          className="tex-hovercard__message-btn"
          onClick={() => {
            if (user.username) {
              navigate(
                directMessagesPath(
                  user.username,
                  conversationId(user.id, profile.userId),
                  profile.username,
                ),
              );
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          Message
        </button>
      )}
      {import.meta.env.VITE_ENABLE_VOUCHING === 'true' && (
        <VouchButton
          targetUserId={profile.userId}
          targetUsername={profile.username}
          viewerUserId={user?.id}
        />
      )}
    </>
  );
}

export function Hovercard({
  state,
  position,
  cardHandlers,
  onFollow,
  onUnfollow,
}: HovercardProps) {
  if (!state.visible) return null;

  return ReactDOM.createPortal(
    <div
      className={`tex-hovercard${state.visible ? ' tex-hovercard--visible' : ''}`}
      style={{ top: position.top, left: position.left }}
      onMouseEnter={cardHandlers.onMouseEnter}
      onMouseLeave={cardHandlers.onMouseLeave}
    >
      {state.loading && <SkeletonContent />}
      {state.error && (
        <p className="tex-hovercard__error">Could not load profile</p>
      )}
      {state.profile && (
        <ProfileContent
          profile={state.profile}
          onFollow={onFollow}
          onUnfollow={onUnfollow}
        />
      )}
    </div>,
    document.body,
  );
}
